import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useUser } from "../contexts/UserContext";
import { motion } from "framer-motion";
import Footer from '../components/Footer'
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from '../utils/printHelper';
import {
  CalendarDaysIcon,
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function LeaveRequest() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const printRef = useRef();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [form, setForm] = useState({
    type: "cuti",
    startDate: "",
    endDate: "",
    reason: "",
    doctorNote: null,
    doctorNoteUrl: "",
  });

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");

  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage("");
      setToastType("");
    }, 3000);
  };

  // Fetch leave requests
  useEffect(() => {
    if (userInfo?.id) {
      fetchRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from("leave_requests")
        .select("*")
      
      // Admin bisa melihat semua, user biasa hanya milik sendiri
      if (userInfo.role !== 'admin') {
        query = query.eq("user_id", userInfo.id)
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch user info untuk admin
      let dataWithUsers = data;
      if (userInfo.role === 'admin' && data && data.length > 0) {
        const userIds = [...new Set(data.map(item => item.user_id))];
        const { data: usersData } = await supabase
          .from("users")
          .select("id, full_name, email")
          .in("id", userIds);
        
        dataWithUsers = data.map(item => ({
          ...item,
          user: usersData?.find(u => u.id === item.user_id) || null
        }));
      }

      const transformed = dataWithUsers.map((item) => ({
        id: item.id,
        type: item.type,
        startDate: item.start_date,
        endDate: item.end_date,
        reason: item.reason,
        status: item.status,
        createdAt: item.created_at,
        doctorNoteUrl: item.doctor_note_url,
        rejectionNote: item.rejection_note,
        rejectedBy: item.rejected_by,
        rejectedAt: item.rejected_at,
        user: item.user,
      }));

      setRequests(transformed);
    } catch (error) {
      console.error("Error fetching requests:", error);
      showToast("Gagal memuat data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.startDate || !form.endDate || !form.reason) {
      showToast("Semua field wajib diisi", "error");
      return;
    }

    if (form.startDate > form.endDate) {
      showToast("Tanggal mulai tidak boleh lebih dari tanggal selesai", "error");
      return;
    }

    setIsLoading(true);

    try {
      // Auto-approve untuk admin
      const isAdmin = userInfo?.role === "admin";
      
      let doctorNoteUrl = form.doctorNoteUrl;

      // Upload surat dokter jika ada
      if (form.doctorNote) {
        const fileExt = form.doctorNote.name.split('.').pop();
        const fileName = `${userInfo.id}_${Date.now()}.${fileExt}`;
        const filePath = `doctor-notes/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, form.doctorNote);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          showToast("Gagal mengupload surat dokter", "error");
          setIsLoading(false);
          return;
        }

        const { data: urlData } = supabase.storage
          .from('documents')
          .getPublicUrl(filePath);
        
        doctorNoteUrl = urlData.publicUrl;
      }
      
      const payload = {
        user_id: userInfo.id,
        type: form.type,
        start_date: form.startDate,
        end_date: form.endDate,
        reason: form.reason,
        doctor_note_url: doctorNoteUrl,
        status: isAdmin ? "approved" : "pending",
      };

      if (editingId) {
        const { error } = await supabase
          .from("leave_requests")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
        showToast("Berhasil mengupdate pengajuan");
      } else {
        const { data, error } = await supabase
          .from("leave_requests")
          .insert([payload])
          .select()

        if (error) throw error;
        
        if (isAdmin) {
          showToast("Berhasil mengajukan dan langsung disetujui (Admin)", "success");
        } else {
          showToast("Berhasil mengajukan " + (form.type === "cuti" ? "cuti" : "izin sakit"));
        }
      }

      fetchRequests();
      closeModal();
    } catch (error) {
      console.error("Error:", error);
      showToast("Gagal menyimpan data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (request) => {
    setForm({
      type: request.type,
      startDate: request.startDate,
      endDate: request.endDate,
      reason: request.reason,
    });
    setEditingId(request.id);
    setShowModal(true);
  };

  const handleView = (request) => {
    setViewData(request);
    setShowViewModal(true);
  };

  const handleApprove = async (requestId) => {
    try {
      const { error } = await supabase
        .from('leave_requests')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;
      
      showToast("✅ Permohonan disetujui", "success");
      fetchRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      showToast("Gagal menyetujui permohonan", "error");
    }
  };

  const handleReject = async (requestId) => {
    setRejectingId(requestId);
    setShowRejectModal(true);
  };

  const submitRejection = async () => {
    if (!rejectionNote.trim()) {
      showToast("Mohon masukkan alasan penolakan", "error");
      return;
    }

    try {
      const { error } = await supabase
        .from('leave_requests')
        .update({ 
          status: 'rejected',
          rejection_note: rejectionNote,
          rejected_by: userInfo.full_name,
          rejected_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', rejectingId);

      if (error) throw error;
      
      setShowRejectModal(false);
      setRejectionNote("");
      setRejectingId(null);
      showToast("❌ Permohonan ditolak", "success");
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      showToast("Gagal menolak permohonan", "error");
    }
  };

  const handleExport = () => {
    if (requests.length === 0) {
      showToast("Tidak ada data untuk diekspor", "error");
      return;
    }

    // Create CSV content
    const headers = ["Jenis", "Tanggal Mulai", "Tanggal Selesai", "Durasi (hari)", "Alasan", "Status", "Tanggal Pengajuan"];
    const rows = requests.map(r => [
      r.type === "cuti" ? "Cuti" : "Sakit",
      new Date(r.startDate).toLocaleDateString("id-ID"),
      new Date(r.endDate).toLocaleDateString("id-ID"),
      calculateDays(r.startDate, r.endDate),
      r.reason,
      r.status === "pending" ? "Menunggu" : r.status === "approved" ? "Disetujui" : "Ditolak",
      new Date(r.createdAt).toLocaleDateString("id-ID")
    ]);

    let csvContent = headers.join(",") + "\n";
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(",") + "\n";
    });

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `leave_requests_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast("Data berhasil diekspor");
  };

  const handlePrint = () => {
    if (isMobileDevice()) {
      showToast('💡 Tip: Untuk hasil terbaik di mobile, gunakan Export ke Word', 'info');
      setTimeout(() => {
        handleMobileFriendlyPrint({
          documentTitle: `Daftar_Cuti_Sakit_${userInfo?.full_name || 'Karyawan'}`,
          onAfterPrint: () => showToast('Dokumen berhasil dicetak')
        });
      }, 1000);
    } else {
      handleDesktopPrint();
    }
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Daftar_Cuti_Sakit_${userInfo?.full_name || 'Karyawan'}`,
    onAfterPrint: () => {
      showToast('Dokumen berhasil dicetak');
    }
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus pengajuan ini?")) return;

    try {
      const { error } = await supabase
        .from("leave_requests")
        .delete()
        .eq("id", id);

      if (error) throw error;

      showToast("Berhasil menghapus pengajuan");
      fetchRequests();
    } catch (error) {
      console.error("Error:", error);
      showToast("Gagal menghapus data", "error");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({
      type: "cuti",
      startDate: "",
      endDate: "",
      reason: "",
      doctorNote: null,
      doctorNoteUrl: "",
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      approved: "bg-green-100 text-green-800 border-green-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
    };
    const labels = {
      pending: "Menunggu",
      approved: "Disetujui",
      rejected: "Ditolak",
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden no-print flex flex-col">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
                <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                </div>
              </div>
            </div>
            <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading leave requests...</p>
          </div>
        </div>
      ) : (
        <>
      {/* Soft Futuristic Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/30 via-blue-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-50/20 via-blue-50/20 to-blue-50/20 rounded-full blur-3xl animate-float-slow"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(99, 102,241, 0.02) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
              <div className="relative backdrop-blur-xl bg-gradient-to-r from-blue-600/90 via-blue-600/90 to-blue-600/90 p-6 sm:p-8 text-white rounded-t-3xl shadow-xl border border-white/20 overflow-hidden">
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer pointer-events-none"></div>
                
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 animate-pulse"></div>

                <div className="relative z-10">
                  <div className="flex justify-between items-center flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all duration-200 group "
                        title="Back to Dashboard"
                      >
                        <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                      </button>
                      <div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
                          Form Cuti & Sakit
                        </h1>
                        <p className="text-blue-100 text-xs sm:text-sm mt-1 flex items-center gap-2">
                          <CalendarDaysIcon className="w-4 h-4" />
                          <span>Kelola pengajuan cuti dan izin sakit</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={handleExport}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl shadow-sm font-medium transition-all duration-200 "
                        title="Export ke CSV"
                      >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                        <span className="hidden sm:inline">Export</span>
                      </button>
                      <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl shadow-sm font-medium transition-all duration-200  print:hidden"
                        title="Print"
                      >
                        <PrinterIcon className="w-5 h-5" />
                        <span className="hidden sm:inline">Print</span>
                      </button>
                      <button
                        onClick={() => setShowModal(true)}
                        className="flex items-center gap-2 bg-white hover:bg-blue-50 text-blue-700 px-4 py-2.5 rounded-xl shadow-sm font-medium transition-all duration-200 "
                      >
                        <PlusIcon className="w-5 h-5" />
                        <span>Ajukan Baru</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div ref={printRef} className="bg-white rounded-b-2xl shadow-xl p-6 sm:p-8 printable-content">
                {/* Print Header - only visible when printing */}
                <div className="hidden print:block mb-6">
                  <h2 className="text-2xl font-bold text-center mb-2">Daftar Pengajuan Cuti & Sakit</h2>
                  <p className="text-center text-sm text-slate-600 mb-1">{userInfo?.full_name}</p>
                  <p className="text-center text-sm text-slate-600 mb-4">
                    Dicetak pada: {new Date().toLocaleDateString("id-ID", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })}
                  </p>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 print:hidden">
                  <div className="relative backdrop-blur-xl bg-white/70 p-4 rounded-2xl border border-white/40 shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-yellow-400 to-amber-400 rounded-l-2xl"></div>
                    <div className="relative flex items-center gap-3">
                      <div className="bg-gradient-to-br from-yellow-100 to-amber-100 p-2.5 rounded-xl">
                        <ClockIcon className="w-8 h-8 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 font-medium">Menunggu</p>
                        <p className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-amber-600 bg-clip-text text-transparent">
                          {requests.filter((r) => r.status === "pending").length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="relative backdrop-blur-xl bg-white/70 p-4 rounded-2xl border border-white/40 shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-400 to-emerald-400 rounded-l-2xl"></div>
                    <div className="relative flex items-center gap-3">
                      <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-2.5 rounded-xl">
                        <CheckCircleIcon className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 font-medium">Disetujui</p>
                        <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                          {requests.filter((r) => r.status === "approved").length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="relative backdrop-blur-xl bg-white/70 p-4 rounded-2xl border border-white/40 shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-400 to-rose-400 rounded-l-2xl"></div>
                    <div className="relative flex items-center gap-3">
                      <div className="bg-gradient-to-br from-red-100 to-rose-100 p-2.5 rounded-xl">
                        <XCircleIcon className="w-8 h-8 text-rose-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 font-medium">Ditolak</p>
                        <p className="text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                          {requests.filter((r) => r.status === "rejected").length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table - Desktop */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-50 to-blue-50">
                      <tr>
                        {userInfo?.role === 'admin' && (
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dibuat Oleh</th>
                        )}
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Jenis</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanggal</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Durasi</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Alasan</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {requests.length === 0 ? (
                        <tr>
                          <td colSpan={userInfo?.role === 'admin' ? "7" : "6"} className="px-4 py-8 text-center text-slate-500">
                            Belum ada pengajuan cuti atau sakit
                          </td>
                        </tr>
                      ) : (
                        requests.map((request) => (
                          <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                            {userInfo?.role === 'admin' && (
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-semibold text-sm text-slate-800">{request.user?.full_name || 'N/A'}</p>
                                  <p className="text-xs text-slate-500">{request.user?.email || ''}</p>
                                </div>
                              </td>
                            )}
                            <td className="px-4 py-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                request.type === "cuti"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {request.type === "cuti" ? "Cuti" : "Sakit"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {new Date(request.startDate).toLocaleDateString("id-ID")} - {new Date(request.endDate).toLocaleDateString("id-ID")}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {calculateDays(request.startDate, request.endDate)} hari
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">
                              {request.reason}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                {getStatusBadge(request.status)}
                                {request.status === 'rejected' && request.rejectionNote && (
                                  <span className="text-xs text-red-600 flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    Ada alasan
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 sm:gap-1.5">
                                <button
                                  onClick={() => handleView(request)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Lihat Detail"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </button>
                                {request.status === "pending" && (
                                  <>
                                    {userInfo?.role === 'admin' ? (
                                      <>
                                        <button
                                          onClick={() => handleApprove(request.id)}
                                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                          title="Setujui"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => handleReject(request.id)}
                                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Tolak"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => handleDelete(request.id)}
                                          className="p-2 text-rose-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Hapus"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => handleEdit(request)}
                                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                          title="Edit"
                                        >
                                          <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDelete(request.id)}
                                          className="p-2 text-rose-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Hapus"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Cards - Mobile */}
                <div className="md:hidden space-y-4">
                  {requests.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Belum ada pengajuan cuti atau sakit
                    </div>
                  ) : (
                    requests.map((request) => (
                      <div
                        key={request.id}
                        className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 rounded-xl border border-gray-200 shadow-sm"
                      >
                        {userInfo?.role === 'admin' && request.user && (
                          <div className="mb-3 pb-3 border-b border-gray-200">
                            <p className="text-xs text-slate-500 mb-1">Dibuat oleh:</p>
                            <p className="font-bold text-slate-800">{request.user.full_name}</p>
                            <p className="text-xs text-slate-500">{request.user.email}</p>
                          </div>
                        )}
                        <div className="flex justify-between items-start mb-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            request.type === "cuti"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {request.type === "cuti" ? "Cuti" : "Sakit"}
                          </span>
                          <div className="flex flex-col items-end gap-1">
                            {getStatusBadge(request.status)}
                            {request.status === 'rejected' && request.rejectionNote && (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Ada alasan penolakan
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2 mb-3">
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Tanggal:</span>{" "}
                            {new Date(request.startDate).toLocaleDateString("id-ID")} - {new Date(request.endDate).toLocaleDateString("id-ID")}
                          </p>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Durasi:</span> {calculateDays(request.startDate, request.endDate)} hari
                          </p>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Alasan:</span> {request.reason}
                          </p>
                        </div>
                        <div className="flex gap-1 sm:gap-1.5">
                          <button
                            onClick={() => handleView(request)}
                            className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-colors"
                          >
                            <EyeIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="hidden sm:inline">Lihat</span>
                          </button>
                          {request.status === "pending" && (
                            <>
                              {userInfo?.role === 'admin' ? (
                                <>
                                  <button
                                    onClick={() => handleApprove(request.id)}
                                    className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="hidden sm:inline">Setujui</span>
                                  </button>
                                  <button
                                    onClick={() => handleReject(request.id)}
                                    className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span className="hidden sm:inline">Tolak</span>
                                  </button>
                                  <button
                                    onClick={() => handleDelete(request.id)}
                                    className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-colors"
                                  >
                                    <TrashIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="hidden sm:inline">Hapus</span>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEdit(request)}
                                    className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-colors"
                                  >
                                    <PencilIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="hidden sm:inline">Edit</span>
                                  </button>
                                  <button
                                    onClick={() => handleDelete(request.id)}
                                    className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-colors"
                                  >
                                    <TrashIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="hidden sm:inline">Hapus</span>
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4">
            <div className="bg-white w-full max-w-lg h-auto max-h-[95vh] flex flex-col p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-3 sm:mb-4 flex-shrink-0">
                {editingId ? "Edit Pengajuan" : "Ajukan Cuti / Sakit"}
              </h2>

              <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Jenis</label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 bg-white"
                  >
                    <option value="cuti">Cuti</option>
                    <option value="sakit">Sakit</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Tanggal Mulai</label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        name="startDate"
                        value={form.startDate}
                        onChange={handleChange}
                        className="w-full pl-3 sm:pl-10 pr-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 min-h-[44px] bg-white"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Tanggal Selesai</label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        name="endDate"
                        value={form.endDate}
                        onChange={handleChange}
                        className="w-full pl-3 sm:pl-10 pr-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 min-h-[44px] bg-white"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Alasan</label>
                  <textarea
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                    placeholder="Jelaskan alasan cuti/sakit Anda..."
                  ></textarea>
                </div>

                {/* Upload Surat Dokter (opsional untuk izin sakit) */}
                {form.type === "sakit" && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Surat Dokter <span className="text-gray-500 text-xs">(Opsional)</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setForm({ ...form, doctorNote: file });
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
                    />
                    {form.doctorNote && (
                      <p className="text-sm text-green-600 mt-1">
                        ✓ File terpilih: {form.doctorNote.name}
                      </p>
                    )}
                    {form.doctorNoteUrl && !form.doctorNote && (
                      <a 
                        href={form.doctorNoteUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                      >
                        📄 Lihat surat dokter yang sudah diupload
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                >
                  {isLoading ? "Menyimpan..." : editingId ? "Update" : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Detail Modal */}
        {showViewModal && viewData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4 print:hidden">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[75vh] flex flex-col p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl">
              <div className="flex justify-between items-center mb-3 sm:mb-4 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-800">Detail Pengajuan {viewData.type === "cuti" ? "Cuti" : "Sakit"}</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-600 mb-1">Jenis</p>
                    <p className="font-semibold text-slate-800">
                      <span className={`px-3 py-1 rounded-full text-xs ${
                        viewData.type === "cuti"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-blue-100 text-blue-800"
                      }`}>
                        {viewData.type === "cuti" ? "Cuti" : "Sakit"}
                      </span>
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-600 mb-1">Status</p>
                    <p className="font-semibold text-slate-800">
                      {getStatusBadge(viewData.status)}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Tanggal Mulai</p>
                  <p className="font-semibold text-slate-800">
                    {new Date(viewData.startDate).toLocaleDateString("id-ID", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Tanggal Selesai</p>
                  <p className="font-semibold text-slate-800">
                    {new Date(viewData.endDate).toLocaleDateString("id-ID", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })}
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 mb-1">Total Durasi</p>
                  <p className="font-bold text-2xl text-blue-900">
                    {calculateDays(viewData.startDate, viewData.endDate)} Hari
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">Alasan</p>
                  <p className="text-slate-800 whitespace-pre-wrap">{viewData.reason}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Tanggal Pengajuan</p>
                  <p className="font-semibold text-slate-800">
                    {new Date(viewData.createdAt).toLocaleDateString("id-ID", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </p>
                </div>

                {/* Surat Dokter jika ada */}
                {viewData.doctorNoteUrl && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-semibold mb-2">📄 Surat Dokter</p>
                    <a 
                      href={viewData.doctorNoteUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 hover:underline text-sm"
                    >
                      Klik untuk melihat surat dokter →
                    </a>
                  </div>
                )}

                {/* Alasan Penolakan jika ditolak */}
                {viewData.status === 'rejected' && viewData.rejectionNote && (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <p className="text-sm text-red-800 font-semibold mb-2">❌ Alasan Penolakan</p>
                    <p className="text-slate-800 whitespace-pre-wrap mb-2">{viewData.rejectionNote}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-600 mt-2 pt-2 border-t border-red-200">
                      <span>Ditolak oleh: <strong>{viewData.rejectedBy}</strong></span>
                      {viewData.rejectedAt && (
                        <>
                          <span>•</span>
                          <span>{new Date(viewData.rejectedAt).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {userInfo?.role === "admin" && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800 font-semibold">
                      ℹ️ Info Admin: Pengajuan dari admin langsung disetujui otomatis
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Note Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-800">Alasan Penolakan</h2>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionNote("");
                    setRejectingId(null);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Mohon berikan alasan penolakan <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-400"
                  placeholder="Contoh: Jumlah cuti tahun ini sudah habis, atau tidak ada surat dokter yang dilampirkan..."
                ></textarea>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionNote("");
                    setRejectingId(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={submitRejection}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Tolak Permohonan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toastMessage && (
          <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
            <div
              className={`px-6 py-3 rounded-lg shadow-sm ${
                toastType === "error"
                  ? "bg-red-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              {toastMessage}
            </div>
          </div>
        )}
      </div>
      </main>
      <Footer />
      </>
      )}
    </div>
  );
}
